import { OmniElement, OmniStyle, OmniIcon, css, html } from 'omni-ui';
import { om2alert } from './om2-alert.js';
import { getSSOEnv } from './om2-util.js';

OmniStyle.register();
OmniIcon.register();

/**
 * Opens a modal, renders a form with Om2Form, and results are sent to callback
 * functions.
 *
 * @param {object} opts - The options datastructure
 * @returns {HtmlElement} The html element created for the form modal
 */
export const om2FormModal = opts => {
  const elem = document.createElement('om2-form-modal');
  document.body.appendChild(elem);
  elem.title = opts.title;
  elem.data = opts.data ?? {};
  elem.schema = opts.schema ?? opts.types; // backwards compatible name
  if (opts.uiSchema) elem.uiSchema = opts.uiSchema;
  elem.onlySubmitChanges = !!opts.onlySubmitChanges;
  elem.onSubmit = opts.onSubmit;
  elem.onSuccess = opts.onSuccess;
  elem.onClose = opts.onClose;
  elem.submitButtonText = opts.submitButtonText;
  elem.additionalButtons = opts.additionalButtons;
  elem.hideOverlay = opts.hideOverlay;
  elem.modalWidth = opts.modalWidth;
  if (opts.env) elem.env = opts.env;
  return elem;
};

/**
 * A web component that opens a modal and renders a form using Om2Form.
 * @extends OmniElement
 */
export default class Om2FormModal extends OmniElement {
  constructor() {
    super();
    this.env = getSSOEnv();
    this.ValidationFailedException = Symbol('ValidationFailedException');
    this.waitingToInit = new Set();
  }

  /**
   * @property {string} title - The modal title
   * @property {object} data - Saved / default data to initialize the form
   * @property {array} schema - the schema (list of input fields) property passed to Om2Form
   * @property {object} uiSchema - the uiSchema (layout etc) property passed to Om2Form. Autogenerated if not set.
   * @property {boolean} onlySubmitChanges - Don't pass fields that were unchanged when submitting
   * @property {function} onSubmit - Callback when form is submitted
   * @property {function} onSuccess - Callback after onSubmit returns without error
   * @property {function} onClose - Callback after modal is closed (regardless of why)
   * @property {string} submitButtonText - The text for the save/submit buttom
   * @property {lit-template} additionalButtons - Customize the modal buttons (aside from submit)
   * @property {boolean} hideOverlay - Hide the modal overlay if true
   * @property {string} modalWidth - Override the modal width if set (must include units)
   * @property {string} env - The current environment (dev, qa, etc)
   */
  static get properties() {
    return {
      title: { type: String },
      data: { type: Object },
      schema: { type: Array },
      uiSchema: { type: Object },
      onlySubmitChanges: { type: Boolean },
      onSubmit: { state: true },
      onSuccess: { state: true },
      onClose: { state: true },
      submitButtonText: { type: String },
      additionalButtons: { type: String },
      hideOverlay: { type: Boolean, reflect: true },
      modalWidth: { type: String },
      env: { type: String },

      // Internal use
      isLoading: { state: true },
      waitingToInit: { state: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        div.skeleton.filler {
          height: 63px;
        }
        :host([hideoverlay]) .modal-background {
          background-color: rgb(0, 0, 0, 0); // transparent
        }
      `,
    ];
  }

  static register(name = 'om2-form-modal') {
    return super.register(name, Om2FormModal);
  }

  get form() {
    return this.shadowRoot.getElementById('theform');
  }

  /**
   * "Closing" the modal causes the component to be destroyed and removed from the dom
   */
  close() {
    if (!this.isLoading) {
      this.remove(); // seppuku
      if (this.onClose) this.onClose();
    }
  }

  /**
   * When schema is set and uiSchema is undefined, we auto-generate uiSchema.
   *
   * @param {object} changedProperties
   * @returns {boolean} always return true
   */
  shouldUpdate(changedProperties) {
    /**
     * Helper to get any custom options passed through
     *
     * @param {object} field - The schema field object
     * @param {string} option - The name of the option
     * @returns {object} The value of the option, if any
     */
    function customOption(field, option) {
      // backwards compatible naming
      return field?.formModal?.[option] ?? field?.modalForm?.[option];
    }

    const waitForPromise = key => {
      if (changedProperties.has(key) && typeof this[key]?.then === 'function') {
        this.waitingToInit.add(key);
        this[key].then(value => {
          this[key] = value;
          this.waitingToInit.delete(key);
          this.requestUpdate('waitingToInit');
        });
      }
    };

    waitForPromise('schema');
    waitForPromise('uiSchema');
    waitForPromise('data');

    if (
      (changedProperties.has('waitingToInit') ||
        changedProperties.has('schema')) &&
      this.schema &&
      this.waitingToInit.size === 0 &&
      !this.uiSchema
    ) {
      // Autogenerate the uiSchema based on contents of schema. Each field is in
      // a "half" column and this wraps as needed to fill up the modal
      // (is-multiline on the row allows wrapping).
      //
      // For complex uiSchemas, it can be passed explicitly. Some support is added here
      // to sneak a few customizations through the schema property:
      //   - schema.formModal.fullWidth: allows a field to be full width vs half
      //   - schema.formModal.hideIf: Hide the field based on the value of other fields
      //   - schema.formModal.fields: additional fields to add to schema (used for arrays in form builder)

      let toAdd = {};
      this.uiSchema = [
        {
          type: 'row',
          class: 'is-multiline',
          content: Object.entries(this.schema).map(([id, field]) => {
            const classWidth = customOption(field, 'fullWidth')
              ? 'is-full'
              : 'is-half';
            if (customOption(field, 'fields')) {
              toAdd = { ...toAdd, ...customOption(field, 'fields') };
            }
            const copy = { ...field };

            // We need to special-case arrays a bit
            const uiContent =
              copy.type === 'array'
                ? [
                    {
                      type: 'text',
                      text: copy.title ?? 'Options',
                      class: 'is-size-7 subtitle',
                    },
                    copy,
                  ]
                : [{ type: 'ref', id }];

            const column = {
              type: 'column',
              class: classWidth,
              content: uiContent,
            };

            // Hide the column and disable the field (disabled fields aren't required) if
            // "hideIf" is configured.
            if (customOption(copy, 'hideIf')) {
              column.overrideIf = [
                {
                  if: customOption(copy, 'hideIf'),
                  then: { class: 'is-hidden' },
                },
              ];
            }

            // Remove customizations before om2-form gets it
            delete copy.formModal;

            return column;
          }),
        },
      ];

      this.schema = { ...this.schema, ...toAdd };
    }
    return true;
  }

  /**
   * Trigger submit/save. Closing is disabled while in progress.
   *
   * @async
   */
  async submit() {
    this.isLoading = true;

    try {
      // Clear any errors we might have had
      Object.entries(this.schema).forEach(([, field]) => {
        delete field.error; // eslint-disable-line no-param-reassign
      });

      if (!this.form.validate()) {
        throw this.ValidationFailedException;
      }

      // This will trigger this.save() with all the form data
      await this.form.submit();

      // The save succeeded (no exception thrown), so close the modal
      this.isLoading = false;
      this.close();
    } catch (e) {
      if (e === this.ValidationFailedException) return;
      const response = typeof e.json === 'function' ? await e.json() : e;

      let displayedError = false;
      [response.json?.errors, response.detail?.json].forEach(errorSource => {
        if (errorSource) {
          Object.entries(errorSource).forEach(([field, error]) => {
            if (
              this.schema[field]?.type &&
              this.schema[field]?.type !== 'array'
            ) {
              this.schema = { ...this.schema };
              this.schema[field].error = Array.isArray(error)
                ? error.join(' ')
                : error;
              displayedError = true;
            }
          });
        }
      });
      if (displayedError) return;

      if (response.message) om2alert(response.message);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * The form was submitted and is now calling save() with the data from the form. If
   * onlySubmitChanges is true, unchanged data is removed. Then callbacks are called.
   *
   * @async
   * @param {object} data - The form data
   */
  async save(data) {
    if (this.onlySubmitChanges) {
      // Remove from mapping if value is unchanged
      const isEqual = (a, b) =>
        typeof a === typeof b && typeof a === 'object'
          ? JSON.stringify(a) === JSON.stringify(b) // "good-enough" object equality test
          : a === b;

      Object.keys(data).forEach(key => {
        if (isEqual(this.data[key], data[key])) {
          delete data[key]; // eslint-disable-line no-param-reassign
        }
      });
      if (Object.keys(data).length === 0) {
        om2alert('Nothing to do. No changes were found.', { type: 'info' });
        this.close();
        return;
      }
    }
    const result = await this.onSubmit?.(data);
    if (this.onSuccess) await this.onSuccess(result);
    this.close();
  }

  render() {
    return html`
      <omni-style>
        <div class="modal is-active">
          <div
            class="modal-background"
            @click=${this.close}
            role="presentation"
          ></div>
          <div
            class="modal-content box"
            style=${this.modalWidth ? `width: ${this.modalWidth}` : ''}
          >
            <div class="is-size-2">${this.title}</div>
            <br />
            ${this.waitingToInit.size > 0
              ? html`
                  <div class="columns is-multiline">
                    <div class="column is-full">
                      <div class="skeleton filler"></div>
                    </div>
                    <div class="column is-full">
                      <div class="skeleton filler"></div>
                    </div>
                  </div>
                `
              : html`
                  <om2-form
                    id="theform"
                    .data=${this.data}
                    .schema=${this.schema}
                    .uiSchema=${this.uiSchema}
                    .save=${data => this.save(data)}
                    .env=${this.env}
                    @om2-form-cancel=${e => {
                      e.stopPropagation();
                      this.close();
                    }}
                  >
                    ${this.additionalButtons
                      ? html`<div slot="additional-buttons">
                          ${this.additionalButtons}
                        </div>`
                      : ''}
                    <button
                      @click=${this.submit}
                      slot="submit-button"
                      class="button is-primary ml-3 ${this.isLoading
                        ? 'is-loading'
                        : ''}"
                    >
                      ${this.submitButtonText ?? 'Save'}
                    </button>
                  </om2-form>
                `}
          </div>
        </div>
      </omni-style>
    `;
  }
}

Om2FormModal.register();
